import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Currency, HOUSE_FEE_BPS, MatchRoomMetadata } from '@solante/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MagnifyingGlassIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useWallet } from '@solana/wallet-adapter-react';
import { Buffer } from 'buffer';

import {
  requestCreateMatch,
  confirmMatch,
  SerializedInstruction,
  useOpenMatches,
  useActiveMatches,
  fetchMatchDetails,
  requestJoinIntent,
  confirmJoin,
  requestCancelIntent,
  confirmCancel,
  MatchDetails,
} from '../../api/matches';
import { useClusterStore } from '../../state/clusterStore';
import { formatCurrency, lamportsToAmount, normalizeLamports, shortKey } from '../../utils/format';
import { toast } from 'sonner';
import { MatchList } from '../components/MatchList';
import { useConnection } from '@solana/wallet-adapter-react';
import { Transaction, PublicKey, TransactionInstruction } from '@solana/web3.js';
import { soundManager } from '../../utils/sounds';
import { sendViaRpc } from '../../wallet/sendViaRpc';
import { getActiveMatch, clearActiveMatch, ActiveMatchInfo } from '../../state/matchPersistence';
import { LoadoutSelector } from '../components/LoadoutSelector';

export const MatchmakingPage: React.FC = () => {
  const navigate = useNavigate();
  const { cluster } = useClusterStore();
  const { data: matches, isLoading } = useOpenMatches();
  const { publicKey, connected, signTransaction } = useWallet();
  const walletAddress = useMemo(() => publicKey?.toBase58() ?? null, [publicKey]);
  const { data: activeMatches } = useActiveMatches(walletAddress);
  const [currency, setCurrency] = useState<Currency>('SOL');
  const [amount, setAmount] = useState('0.1');
  const [serverSeed, setServerSeed] = useState<string>(() => crypto.randomUUID().replace(/-/g, ''));
  const { connection } = useConnection();
  const [localResumableMatches, setLocalResumableMatches] = useState<MatchDetails[]>([]);
  const [openMatchDetails, setOpenMatchDetails] = useState<Record<string, MatchDetails>>({});
  const [joiningMatchId, setJoiningMatchId] = useState<string | null>(null);
  const [persistedActiveMatch, setPersistedActiveMatch] = useState<ActiveMatchInfo | null>(null);
  const queryClient = useQueryClient();
  const prevActiveMatchesRef = useRef<MatchDetails[] | undefined>();

  // Check for persisted active match on mount
  useEffect(() => {
    if (!walletAddress) {
      setPersistedActiveMatch(null);
      return;
    }
    const activeMatch = getActiveMatch();
    if (activeMatch && activeMatch.wallet === walletAddress) {
      setPersistedActiveMatch(activeMatch);
    } else {
      setPersistedActiveMatch(null);
    }
  }, [walletAddress]);

  const handleReconnectMatch = () => {
    if (persistedActiveMatch) {
      soundManager.play('select');
      navigate(`/room/${persistedActiveMatch.matchId}`);
    }
  };

  const handleDismissReconnect = () => {
    clearActiveMatch();
    setPersistedActiveMatch(null);
    soundManager.play('click');
  };
  const payoutPreview = useMemo(() => {
    const parsedAmount = parseFloat(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      return null;
    }
    // Total pot is both players' wagers combined
    const totalPot = parsedAmount * 2;
    const houseCut = (totalPot * HOUSE_FEE_BPS) / 10_000;
    return {
      total: totalPot,
      houseFee: houseCut,
      winner: Math.max(totalPot - houseCut, 0),
    };
  }, [amount]);
  const openEscrowStatus = useMemo(
    () =>
      (matches ?? []).reduce(
        (accumulator, match) => {
          const details = openMatchDetails[match.matchId];
          const wallets = details?.players ?? [];
          let locked = wallets.length;
          if (!locked) {
            if (match.status === 'awaitingSecondPlayer') {
              locked = 1;
            } else if (match.status === 'inProgress') {
              locked = 2;
            }
          }
          const total = Math.max(wallets.length, 2);
          accumulator[match.matchId] = {
            locked,
            total,
            wallets,
            status: details?.status ?? match.status,
          };
          return accumulator;
        },
        {} as Record<
          string,
          {
            locked: number;
            total: number;
            wallets: string[];
            status: MatchRoomMetadata['status'];
          }
        >,
      ),
    [matches, openMatchDetails],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!walletAddress) {
      setLocalResumableMatches([]);
      return;
    }
    let cancelled = false;
    const keys = Object.keys(sessionStorage).filter((key) => key.startsWith('solante:room:'));
    if (keys.length === 0) {
      setLocalResumableMatches([]);
      return;
    }
    const loadMatches = async () => {
      const matches = await Promise.all(
        keys.map(async (key) => {
          const matchId = key.substring('solante:room:'.length);
          if (!matchId) return null;
          try {
            const details = await fetchMatchDetails(matchId);
            if (!details.players.includes(walletAddress)) {
              return null;
            }
            if (details.status === 'completed' || details.status === 'cancelled') {
              sessionStorage.removeItem(key);
              return null;
            }
            return { ...details };
          } catch {
            sessionStorage.removeItem(key);
            return null;
          }
        }),
      );
      if (!cancelled) {
        setLocalResumableMatches(matches.filter((match): match is MatchDetails => Boolean(match)));
      }
    };
    loadMatches().catch(() => {
      if (!cancelled) {
        setLocalResumableMatches([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [walletAddress]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!activeMatches?.length) {
      return;
    }
    activeMatches.forEach((match) => {
      if (match.roomId) {
        sessionStorage.setItem(`solante:room:${match.matchId}`, match.roomId);
      }
    });
  }, [activeMatches]);

  useEffect(() => {
    if (!matches?.length) {
      setOpenMatchDetails({});
      return;
    }
    let cancelled = false;
    const load = async () => {
      const pending = await Promise.all(
        matches
          .filter((match) => !openMatchDetails[match.matchId])
          .map(async (match) => {
            try {
              const details = await fetchMatchDetails(match.matchId);
              return [match.matchId, details] as const;
            } catch {
              return null;
            }
          }),
      );
      if (cancelled || pending.length === 0) {
        return;
      }
      setOpenMatchDetails((current) => {
        const next = { ...current } as Record<string, MatchDetails>;
        for (const entry of pending) {
          if (entry) {
            const [matchId, details] = entry;
            next[matchId] = details;
          }
        }
        return next;
      });
    };
    void load();
    return () => {
      cancelled = true;
    };
    // openMatchDetails intentionally omitted to avoid refetch loop; reducer merges latest
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  const resumableMatches = useMemo(() => {
    if (!walletAddress) {
      return [];
    }
    const merged = new Map<string, MatchDetails>();
    activeMatches?.forEach((match) => {
      merged.set(match.matchId, match);
    });
    localResumableMatches.forEach((match) => {
      if (!merged.has(match.matchId)) {
        merged.set(match.matchId, match);
      }
    });
    return Array.from(merged.values()).sort((a, b) => b.createdAt - a.createdAt);
  }, [activeMatches, localResumableMatches, walletAddress]);

  // Detect when an opponent joins and play notification sound
  useEffect(() => {
    if (!activeMatches || !walletAddress) return;

    const prevMatches = prevActiveMatchesRef.current;
    prevActiveMatchesRef.current = activeMatches;

    if (!prevMatches) return;

    activeMatches.forEach((match) => {
      const prevMatch = prevMatches.find((m) => m.matchId === match.matchId);

      // Detect opponent joining: was awaiting, now has 2 players or is inProgress
      if (
        prevMatch &&
        prevMatch.status === 'awaitingSecondPlayer' &&
        (match.status === 'inProgress' || match.players.length === 2) &&
        match.creator === walletAddress
      ) {
        soundManager.play('matchReady');
        toast.success('An opponent has joined your match!', { duration: 5000 });
      }
    });
  }, [activeMatches, walletAddress]);

  const cancelMutation = useMutation({
    mutationFn: async (matchId: string) => {
      if (!publicKey) {
        throw new Error('Connect wallet to cancel match');
      }
      if (!connection) {
        throw new Error('Connection unavailable');
      }

      const cancelToastId = toast.loading('Step 1/3: Preparing cancellation…');

      try {
        const walletBase58 = walletAddress ?? publicKey.toBase58();
        const intent = await requestCancelIntent(matchId, walletBase58);

        toast.loading('Step 2/3: Approve the transaction in your wallet.', {
          id: cancelToastId,
        });

        const instructions = intent.instructions.map(
          (instruction: SerializedInstruction) =>
            new TransactionInstruction({
              programId: new PublicKey(instruction.programId),
              keys: instruction.keys.map((key) => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: key.isSigner,
                isWritable: key.isWritable,
              })),
              data: Buffer.from(instruction.data, 'base64'),
            }),
        );

        const transaction = new Transaction().add(...instructions);
        transaction.feePayer = publicKey;
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;

        // Simulate via our RPC first to catch actual program errors
        // (logs the real error instead of Phantom's vague "User rejected")
        const simResult = await connection.simulateTransaction(transaction);
        if (simResult.value.err) {
          console.error('[Cancel] Simulation failed:', JSON.stringify(simResult.value.err));
          console.error('[Cancel] Simulation logs:', simResult.value.logs);
        }

        const signature = await sendViaRpc(transaction, connection, { signTransaction });

        toast.loading('Step 3/3: Confirming cancellation…', {
          id: cancelToastId,
        });

        await connection.confirmTransaction(
          { signature, blockhash, lastValidBlockHeight },
          'confirmed',
        );

        await confirmCancel(matchId, walletBase58, signature);

        toast.success('Match cancelled - funds returned to your wallet!', {
          id: cancelToastId,
          duration: 5000,
        });

        return matchId;
      } catch (caughtError) {
        const normalizedError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to cancel match. Please try again.');
        toast.error(normalizedError.message, { id: cancelToastId });
        (normalizedError as Error & { __toastHandled?: boolean }).__toastHandled = true;
        throw normalizedError;
      }
    },
    onSuccess: () => {
      soundManager.play('click');
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    },
    onError: (error: Error & { __toastHandled?: boolean }) => {
      soundManager.play('error');
      if (!error.__toastHandled) {
        toast.error(error.message);
      }
    },
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (!publicKey) {
        throw new Error('Connect wallet to create a match');
      }
      if (!connection) {
        throw new Error('Connection unavailable');
      }

      const createToastId = toast.loading('Step 1/4: Preparing wager intent…');

      try {
        const walletBase58 = walletAddress ?? publicKey.toBase58();
        const intent = await requestCreateMatch({
          amount,
          currency,
          serverSeed,
          cluster,
          creatorWallet: walletBase58,
        });

        toast.loading('Step 2/4: Approve the transaction in your wallet.', {
          id: createToastId,
        });

        const instructions = intent.instructions.map(
          (instruction: SerializedInstruction) =>
            new TransactionInstruction({
              programId: new PublicKey(instruction.programId),
              keys: instruction.keys.map((key) => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: key.isSigner,
                isWritable: key.isWritable,
              })),
              data: Buffer.from(instruction.data, 'base64'),
            }),
        );

        const transaction = new Transaction().add(...instructions);
        transaction.feePayer = publicKey;
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;

        // Pre-simulate via our RPC to log actual errors
        const simResult = await connection.simulateTransaction(transaction);
        if (simResult.value.err) {
          console.error('[Create] Simulation failed:', JSON.stringify(simResult.value.err));
          console.error('[Create] Simulation logs:', simResult.value.logs);
        } else {
          console.log('[Create] Simulation succeeded');
        }

        const signature = await sendViaRpc(transaction, connection, { signTransaction });

        toast.loading('Step 3/4: Confirming escrow on-chain…', {
          id: createToastId,
        });

        await connection.confirmTransaction(
          { signature, blockhash, lastValidBlockHeight },
          'confirmed',
        );

        toast.loading('Step 4/4: Finalizing match…', {
          id: createToastId,
        });

        const confirmation = await confirmMatch(intent.matchId, signature);
        if (confirmation.roomId) {
          sessionStorage.setItem(`solante:room:${confirmation.matchId}`, confirmation.roomId);
        }

        toast.success('Match created! Connecting to room…', {
          id: createToastId,
          duration: 3000,
        });

        return confirmation;
      } catch (caughtError) {
        const normalizedError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to create match. Please try again.');
        toast.error(normalizedError.message, { id: createToastId });
        (normalizedError as Error & { __toastHandled?: boolean }).__toastHandled = true;
        throw normalizedError;
      }
    },
    onSuccess: ({ matchId }) => {
      soundManager.play('powerup');
      navigate(`/room/${matchId}`);
    },
    onError: (error: Error & { __toastHandled?: boolean }) => {
      soundManager.play('error');
      if (!error.__toastHandled) {
        toast.error(error.message);
      }
    },
  });

  const joinMutation = useMutation({
    mutationFn: async (match: MatchRoomMetadata) => {
      if (!publicKey) {
        throw new Error('Connect wallet to join a match');
      }
      if (!connection) {
        throw new Error('Connection unavailable');
      }
      const joinToastId = toast.loading('Step 1/4: Preparing join intent…');

      try {
        const walletBase58 = walletAddress ?? publicKey.toBase58();
        const details = await fetchMatchDetails(match.matchId);
        if (details.wager.currency !== 'SOL') {
          throw new Error('Only SOL wagers are supported currently.');
        }

        toast.loading('Step 2/4: Approve the transaction in your wallet.', {
          id: joinToastId,
        });

        const intent = await requestJoinIntent(match.matchId, walletBase58);
        const instructions = intent.instructions.map(
          (instruction: SerializedInstruction) =>
            new TransactionInstruction({
              programId: new PublicKey(instruction.programId),
              keys: instruction.keys.map((key) => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: key.isSigner,
                isWritable: key.isWritable,
              })),
              data: Buffer.from(instruction.data, 'base64'),
            }),
        );

        const transaction = new Transaction().add(...instructions);
        transaction.feePayer = publicKey;
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;

        const signature = await sendViaRpc(transaction, connection, { signTransaction });

        toast.loading('Step 3/4: Confirming stakes on-chain…', { id: joinToastId });

        await connection.confirmTransaction(
          { signature, blockhash, lastValidBlockHeight },
          'confirmed',
        );

        toast.loading('Step 4/4: Finalizing join…', { id: joinToastId });

        await confirmJoin(match.matchId, walletBase58, signature);

        setOpenMatchDetails((current) => ({
          ...current,
          [match.matchId]: {
            ...details,
            players: Array.from(new Set([...(details.players ?? []), walletBase58])),
            status: 'inProgress',
          },
        }));

        toast.success('Joined match! Connecting to room…', { id: joinToastId, duration: 3000 });

        return match.matchId;
      } catch (caughtError) {
        const normalizedError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to join match. Please try again.');
        toast.error(normalizedError.message, { id: joinToastId });
        (normalizedError as Error & { __toastHandled?: boolean }).__toastHandled = true;
        throw normalizedError;
      }
    },
    onSuccess: (matchId) => {
      soundManager.play('powerup');
      navigate(`/room/${matchId}`);
    },
    onError: (error: Error & { __toastHandled?: boolean }) => {
      soundManager.play('error');
      if (!error.__toastHandled) {
        toast.error(error.message);
      }
    },
    onSettled: () => {
      setJoiningMatchId(null);
    },
  });

  return (
    <div className="space-y-10 pt-8">
      {/* Reconnection Banner */}
      <AnimatePresence>
        {persistedActiveMatch && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="arcade-panel p-4 border-2 border-amber-400/60 bg-amber-500/10"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <ExclamationTriangleIcon className="h-7 w-7 text-amber-400 animate-pulse" />
                <div>
                  <h3 className="font-display text-lg font-bold text-amber-200">
                    Active Match Found
                  </h3>
                  <p className="text-sm text-white/70">
                    You have an active match in progress. Rejoin to avoid losing your wager.
                  </p>
                  <p className="text-xs text-white/50 mt-1">
                    Match: {persistedActiveMatch.matchId.slice(0, 8)}...
                    {persistedActiveMatch.wagerAmount && persistedActiveMatch.currency && (
                      <>
                        {' '}
                        • Wager:{' '}
                        {formatCurrency(
                          lamportsToAmount(
                            normalizeLamports(persistedActiveMatch.wagerAmount),
                            persistedActiveMatch.currency as Currency,
                          ),
                          persistedActiveMatch.currency as Currency,
                        )}
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleReconnectMatch}
                  className="arcade-btn px-6 py-2"
                >
                  REJOIN MATCH
                </button>
                <button
                  type="button"
                  onClick={handleDismissReconnect}
                  className="rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm text-white/70 hover:bg-white/20"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.section
        className="arcade-panel pixel-corners grid gap-6 rounded-xl p-4 sm:p-8 lg:grid-cols-[2fr,3fr]"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <h2 className="neon-text font-display text-2xl font-semibold">Create Wager</h2>
          <p className="mt-2 text-sm text-white/70">
            Choose your currency, enter the buy-in, and lock a server seed hash. Opponents must
            commit a client seed before the first shuffle.
          </p>
          <form
            className="mt-6 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              mutation.mutate();
            }}
          >
            <label className="block text-sm">
              <span className="text-white/60">Currency</span>
              <select
                value={currency}
                onChange={(event) => setCurrency(event.target.value as Currency)}
                className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-arcade-cyan/50 focus:outline-none focus:ring-1 focus:ring-arcade-cyan/30"
              >
                <option value="SOL">SOL</option>
                <option value="USDC" disabled>
                  USDC (soon)
                </option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-white/60">Amount ({currency})</span>
              <input
                required
                min="0.01"
                step="0.01"
                type="number"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-arcade-cyan/50 focus:outline-none focus:ring-1 focus:ring-arcade-cyan/30"
              />
            </label>
            <label className="block text-sm">
              <span className="text-white/60">Server Seed (kept secret until settlement)</span>
              <input
                required
                minLength={32}
                value={serverSeed}
                onChange={(event) => setServerSeed(event.target.value)}
                className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-mono text-xs text-neon-400 focus:border-arcade-cyan/50 focus:outline-none focus:ring-1 focus:ring-arcade-cyan/30"
              />
            </label>
            <div className="rounded-2xl border border-arcade-cyan/30 bg-arcade-cyan/10 p-4 text-sm">
              <p className="font-semibold text-arcade-cyan">Payout Preview</p>
              {payoutPreview ? (
                <ul className="mt-2 space-y-1 text-white/80">
                  <li>Total Pot: {formatCurrency(payoutPreview.total, currency)}</li>
                  <li>House (3%): {formatCurrency(payoutPreview.houseFee, currency)}</li>
                  <li>Winner (97%): {formatCurrency(payoutPreview.winner, currency)}</li>
                </ul>
              ) : (
                <p className="mt-1 text-white/60">Enter an amount to view the payout split.</p>
              )}
            </div>
            <LoadoutSelector />
            <button
              type="submit"
              disabled={mutation.isPending || !connected}
              className="arcade-btn pixel-corners flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-display text-xs font-semibold uppercase tracking-wide text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {connected ? (mutation.isPending ? 'Creating…' : 'Create Match') : 'Connect Wallet'}
            </button>
          </form>
        </div>
        <div className="rounded-3xl border border-white/10 bg-black/40 p-6">
          {resumableMatches.length > 0 && (
            <div className="mb-6 space-y-3 rounded-2xl border border-neon-500/30 bg-neon-500/10 p-4">
              <header className="text-sm font-semibold uppercase tracking-wide text-neon-300">
                Resume Match
              </header>
              <ul className="space-y-3">
                <AnimatePresence initial={false}>
                  {resumableMatches.map((match) => {
                    const amount = lamportsToAmount(
                      normalizeLamports(match.wager.amountLamports),
                      match.wager.currency,
                    );
                    const statusText =
                      match.status === 'awaitingSecondPlayer'
                        ? 'Waiting for opponent'
                        : match.status === 'inProgress'
                          ? 'In progress'
                          : match.status;
                    const lockedCount = match.players.length;
                    const totalPlayers = Math.max(lockedCount || 2, 2);
                    const walletSummary = match.players.length
                      ? match.players.map((wallet) => shortKey(wallet)).join(', ')
                      : 'Awaiting stakes';
                    return (
                      <motion.li
                        key={match.matchId}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neon-500/20 bg-black/40 px-4 py-3 text-xs text-white/70"
                      >
                        <div>
                          <p className="text-sm font-semibold text-white/80">
                            Match {shortKey(match.matchId)}
                          </p>
                          <p>{formatCurrency(amount, match.wager.currency)}</p>
                          <p className="text-[11px] uppercase tracking-wide text-neon-300/80">
                            Stakes Locked: {Math.min(lockedCount, totalPlayers)}/{totalPlayers}
                          </p>
                          <p className="text-[11px] text-white/50">{walletSummary}</p>
                          <p className="text-[11px] uppercase tracking-wide text-white/40">
                            {statusText}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {match.status === 'awaitingSecondPlayer' &&
                            match.creator === walletAddress && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cancelMutation.mutate(match.matchId);
                                }}
                                disabled={cancelMutation.isPending}
                                className="rounded-full border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-rose-300 transition hover:bg-rose-500/20 disabled:opacity-50"
                              >
                                {cancelMutation.isPending ? 'Cancelling…' : 'Cancel'}
                              </button>
                            )}
                          <button
                            type="button"
                            onClick={() => navigate(`/room/${match.matchId}`)}
                            className="rounded-full bg-neon-500 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-black transition hover:bg-neon-400"
                          >
                            Resume
                          </button>
                        </div>
                      </motion.li>
                    );
                  })}
                </AnimatePresence>
              </ul>
            </div>
          )}
          <header className="flex items-center gap-2 text-sm font-semibold text-white/80">
            <MagnifyingGlassIcon className="h-5 w-5 text-neon-500" />
            Open Matches
          </header>
          <div className="mt-4 h-[300px] overflow-y-auto pr-2 sm:h-[420px]">
            <MatchList
              matches={matches}
              loading={isLoading}
              emptyMessage="No open matches yet. Create one!"
              onJoin={(match) => {
                if (!joinMutation.isPending) {
                  setJoiningMatchId(match.matchId);
                  joinMutation.mutate(match);
                }
              }}
              isJoining={joinMutation.isPending}
              joiningMatchId={joiningMatchId}
              escrowStatus={openEscrowStatus}
            />
          </div>
        </div>
      </motion.section>
    </div>
  );
};
